/**
 * Best Next Move Engine V2 — Types
 *
 * Type definitions for deterministic move selection engine.
 * Engine suggests moves only. It does not execute or approve.
 */

/**
 * Move definition (generic, can be any move type)
 */
export interface Move {
  moveId: string;
  moveType: string;
  description: string;
  dependencies: string[]; // Artifact IDs or move IDs this move depends on
  unblocks: string[]; // Move IDs or blocking conditions this move unblocks
  riskLevel: 'low' | 'medium' | 'high';
  requiredAuthority?: string; // Authority level required
  estimatedDurationMs?: number;
  metadata?: Record<string, unknown>;
}

/**
 * System state snapshot
 */
export interface SystemState {
  timestamp: number;
  availableArtifacts: string[]; // Artifact IDs currently available
  activeBlockers: string[]; // Current blocking conditions
  lifecycleStage?: string;
  contextHash?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Blocked move with reason
 */
export interface BlockedMove {
  moveId: string;
  reason: string;
  blockedAt: number;
}

/**
 * Strategy memory entry for a move
 */
export interface StrategyMemoryEntry {
  moveId: string;
  successCount: number;
  failureCount: number;
  lastExecutionTimestamp?: number;
  averageDurationMs?: number;
  successRate: number; // 0-100
  notes?: string[];
}

/**
 * Execution history entry
 */
export interface ExecutionHistoryEntry {
  moveId: string;
  timestamp: number;
  success: boolean;
  durationMs: number;
  producedArtifacts: string[];
}

/**
 * Engine input
 */
export interface BestNextMoveEngineInput {
  systemState: SystemState;
  availableMoves: Move[];
  blockedMoves: BlockedMove[];
  strategyMemory?: Map<string, StrategyMemoryEntry>;
  executionHistory?: ExecutionHistoryEntry[];
}

/**
 * Move scoring criteria
 */
export interface MoveScoringCriteria {
  dependencySatisfaction: number; // 0-100: how many dependencies are satisfied
  unblockPotential: number; // 0-100: how many blockers this move resolves
  riskScore: number; // 0-100: lower is safer
  successRate: number; // 0-100: past success rate from strategy memory
  priority: number; // 0-100: intrinsic priority
}

/**
 * Scored move with criteria
 */
export interface ScoredMove {
  move: Move;
  score: number;
  criteria: MoveScoringCriteria;
  confidence: number; // 0-100: deterministic confidence level
  reason?: string; // Human-readable explanation for this move
  whyNotSelected?: string; // Why this move was not selected (for alternatives)
}

/**
 * Engine output
 */
export interface BestNextMoveEngineOutput {
  selectedMove: Move | null;
  reason: string; // Human-readable explanation for selected move
  confidence: number; // 0-100: deterministic confidence level
  alternatives: ScoredMove[]; // Top N alternatives (for "see alternatives" UI)
  scoringDetails: {
    totalMovesConsidered: number;
    inadmissibleFiltered: number;
    blockedFiltered: number;
    scoredMoves: number;
  };
  timestamp: number;
  engineVersion: string;
}

/**
 * Engine configuration
 */
export interface BestNextMoveEngineConfig {
  enableDependencyScoring: boolean;
  enableUnblockScoring: boolean;
  enableRiskScoring: boolean;
  enableStrategyMemory: boolean;
  enableExecutionHistory: boolean;
  maxAlternatives: number; // Number of alternatives to include in output
  requireAuthorityCheck: boolean; // Fail-closed if move requires unavailable authority
  deterministicSeed?: string; // For reproducibility (though engine is deterministic by design)
}

/**
 * Default engine configuration
 */
export const DEFAULT_ENGINE_CONFIG: BestNextMoveEngineConfig = {
  enableDependencyScoring: true,
  enableUnblockScoring: true,
  enableRiskScoring: true,
  enableStrategyMemory: true,
  enableExecutionHistory: true,
  maxAlternatives: 3,
  requireAuthorityCheck: true,
};

/**
 * Admissibility rule
 */
export interface AdmissibilityRule {
  ruleId: string;
  description: string;
  check: (move: Move, systemState: SystemState) => { admissible: boolean; reason?: string };
}

/**
 * Engine validation result
 */
export interface EngineValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}
